import React from "react";

import { Collapse, Spin, Table as AntTable, Card, Space, Typography, Empty } from "antd";
import { FlagOutlined, EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../shared/utils/Util";
import { getFetchT } from "../../shared/utils/TypedUtil";
import { RestaurantFeatureFlag } from "../../types/model";
import { useAntdTheme, useNotification } from "../../shared/hooks";

import { RestaurantFeatureFlagForm } from "./Form";

const { Panel } = Collapse;
const { Title, Text } = Typography;

type RestaurantFeatureFlagEditorProps = {
  restaurantId: string;
};

export const RestaurantFeatureFlagEditor = (props: RestaurantFeatureFlagEditorProps) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { notifyError } = useNotification();
  const [flags, setFlags] = React.useState<ReadonlyArray<RestaurantFeatureFlag> | undefined>(
    undefined
  );

  const refreshFlags = () => {
    getFetchT<{ flags: ReadonlyArray<RestaurantFeatureFlag> }>(
      `${API_URL}/v1/r/${props.restaurantId}/feature_flags`,
      (result) => setFlags(result.flags),
      (err) => {
        notifyError(t("failed_to_fetch_flags"), err);
      }
    );
  };

  // Load current set of announcemets.
  React.useEffect(refreshFlags, [props.restaurantId]);

  if (flags === undefined) {
    // Still loading
    return <Spin size="large" />;
  }

  const onChange = () => {
    // Set flags to undefined to show loading icon again.
    setFlags(undefined);
    refreshFlags();
  };

  const columns: any = [
    {
      title: t("name"),
      dataIndex: "flag_name",
    },
    {
      title: t("enabled"),
      dataIndex: "enabled",
      render: (text: string, record: RestaurantFeatureFlag) =>
        record.enabled ? t("true") : t("false"),
    },
  ];

  return (
    <div>
      {/* Update Feature Flag */}
      <Card
        title={
          <Space>
            <EditOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("update_flag")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
          marginBottom: token.marginXL,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <RestaurantFeatureFlagForm restaurantId={props.restaurantId} onSuccess={onChange} />
      </Card>

      {/* Current Feature Flags */}
      <Card
        title={
          <Space>
            <FlagOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("current_flags")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        {!flags || flags.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">{t("no_flags_available")}</Text>}
          />
        ) : (
          <AntTable
            columns={columns}
            dataSource={flags}
            rowKey="flag_name"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `${t("total")} ${total} ${t("flags")}`,
            }}
            style={{ borderRadius: token.borderRadiusLG }}
          />
        )}
      </Card>
    </div>
  );
};
